import axios from 'axios';
import mock from './mockApi';
import { shouldAttemptRefresh } from '../utils/authRefresh';

// If REACT_APP_MOCK=true, use the mock API implementation so the UI runs
// without a backend. Otherwise create a real axios instance.
const USE_MOCK = process.env.REACT_APP_MOCK === 'true';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Флаг для предотвращения множественных refresh запросов
let isRefreshing = false;
// Очередь запросов, ожидающих обновления токена
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Create a real axios instance (used when not mocking)
const realApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Функция для обновления токена
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  
  console.log('[API] Refreshing access token...');
  
  // Используем fetch вместо axios чтобы избежать interceptors
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken })
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to refresh token');
  }
  
  const data = await response.json();
  
  if (data.token) {
    localStorage.setItem('token', data.token);
    console.log('[API] New access token saved');
  }
  
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken);
    console.log('[API] New refresh token saved');
  }
  
  if (data.user) {
    localStorage.setItem('user', JSON.stringify(data.user));
  }
  
  window.dispatchEvent(new Event('authChange'));
  
  return data.token;
};

// Функция для очистки сессии и редиректа на логин
const clearSessionAndRedirect = () => {
  console.log('[API] Clearing session and redirecting to login...');
  
  const keysToPreserve = ['pendingFcmToken'];
  const preservedData = {};
  keysToPreserve.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) preservedData[key] = value;
  });
  
  localStorage.clear();
  
  Object.keys(preservedData).forEach(key => {
    localStorage.setItem(key, preservedData[key]);
  });
  
  window.dispatchEvent(new Event('authChange'));
  window.dispatchEvent(new CustomEvent('sessionExpired'));
};

if (!USE_MOCK) {
  // Request interceptor для realApi
  realApi.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor для realApi
  realApi.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (shouldAttemptRefresh({ status: error.response?.status, url: originalRequest?.url, retried: originalRequest?._retry, hasRefreshToken: !!localStorage.getItem('refreshToken') })) {
        console.log('[API] Got 401, checking refresh token...');
        
        if (isRefreshing) {
          // Если уже идет refresh, добавляем запрос в очередь
          console.log('[API] Refresh in progress, queuing request...');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return realApi(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          console.log('[API] Token refreshed successfully');
          
          processQueue(null, newToken);
          
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return realApi(originalRequest);
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError.message);
          processQueue(refreshError, null);
          clearSessionAndRedirect();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      
      return Promise.reject(error);
    }
  );

  // ГЛОБАЛЬНЫЙ interceptor для ВСЕХ axios запросов (включая прямые axios.get/post)
  axios.interceptors.request.use(
    (config) => {
      // Добавляем токен только для запросов к нашему API
      if (config.url?.includes(BASE_URL) || config.url?.includes('backend.thinklink.pro')) {
        const token = localStorage.getItem('token');
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // Проверяем что это запрос к нашему API и получили 401
      const isOurApi = originalRequest?.url?.includes(BASE_URL) || 
                       originalRequest?.url?.includes('backend.thinklink.pro');
      
      if (isOurApi && shouldAttemptRefresh({ status: error.response?.status, url: originalRequest?.url, retried: originalRequest?._retry, hasRefreshToken: !!localStorage.getItem('refreshToken') })) {
        console.log('[API Global] Got 401 for:', originalRequest.url);
        
        if (isRefreshing) {
          console.log('[API Global] Refresh in progress, queuing request...');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          console.log('[API Global] Token refreshed successfully');
          
          processQueue(null, newToken);
          
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error('[API Global] Token refresh failed:', refreshError.message);
          processQueue(refreshError, null);
          clearSessionAndRedirect();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      
      return Promise.reject(error);
    }
  );
}

const impl = USE_MOCK ? mock : realApi;

// Named exports used across the app
export const getReferrals = () => impl.get('/referrals');

export const getReferralStats = () => impl.get('/referrals/stats');

export const generateReferralLink = () => impl.post('/referrals/link');

export const registerWithReferral = (referralCode, userData) =>
  impl.post('/referrals/register', { referralCode, userData });

export const awardReferralPoints = (userId, points, reason) =>
  impl.post('/referrals/award-points', { userId, points, reason });

export default impl;